"use client"

import { addToCart } from "@lib/data/cart"
import { HttpTypes } from "@medusajs/types"
import Divider from "@modules/common/components/divider"
import OptionSelect from "@modules/products/components/product-actions/option-select"
import { useParams } from "next/navigation"
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import ProductPrice from "../product-price"

type ProductActionsProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  disabled?: boolean
  setOptions: Dispatch<SetStateAction<Record<string, string | undefined>>>
  options: Record<string, string | undefined>
}

const optionsAsKeymap = (
  variantOptions: HttpTypes.StoreProductVariant["options"]
) => {
  return variantOptions?.reduce((acc: Record<string, string>, varopt: any) => {
    acc[varopt.option.title] = varopt.value
    return acc
  }, {})
}

export default function ProductActions({
  product,
  disabled,
  setOptions,
  options,
}: ProductActionsProps) {
  const [isAdding, setIsAdding] = useState(false)
  const countryCode = useParams().countryCode as string

  // If there is only 1 variant, preselect the options
  useEffect(() => {
    if (product.variants && product.variants.length > 0) {
      const variantOptions = optionsAsKeymap(product.variants[0].options)
      setOptions(variantOptions ?? {})
    }
  }, [product.variants])


  const selectedVariant = useMemo(() => {
    if (!product.variants || product.variants.length === 0) {
      return
    }

    return product.variants[0]
  }, [product.variants, options])

  // update the options when a variant is selected
  const setOptionValue = (title: string, value: string) => {
    setOptions((prev) => ({
      ...prev,
      [title]: value,
    }))
  }

  // Randomize all color options
  const randomizeOptions = () => {
    const newOptions = { ...options }

    // For each option in the product
    product.options?.forEach((option) => {
      if (option.title?.startsWith("Color") || option.title?.startsWith("Base")) {
        // Get available values for this option
        const availableValues = option.values?.map(v => v.value) || []

        if (availableValues.length > 0) {
          // Select a random value from available values
          const randomIndex = Math.floor(Math.random() * availableValues.length)
          const randomValue = availableValues[randomIndex]

          // Update the option
          newOptions[option.title || ""] = randomValue
        }
      }
    })

    // Update all options at once
    setOptions(newOptions)
  }

  //check if the selected options produce a valid variant
  const isValidVariant = true

  // check if the selected variant is in stock
  const inStock = useMemo(() => {
    // If we don't manage inventory, we can always add to cart
    if (selectedVariant && !selectedVariant.manage_inventory) {
      return true
    }

    // If we allow back orders on the variant, we can add to cart
    if (selectedVariant?.allow_backorder) {
      return true
    }

    // If there is inventory available, we can add to cart
    if (
      selectedVariant?.manage_inventory &&
      (selectedVariant?.inventory_quantity || 0) > 0
    ) {
      return true
    }

    // Otherwise, we can't add to cart
    return false
  }, [selectedVariant])

  const actionsRef = useRef<HTMLDivElement>(null)

  // add the selected variant to the cart
  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return null

    setIsAdding(true)

    await addToCart({
      variantId: selectedVariant.id,
      quantity: 1,
      countryCode,
      metadata: options
    })

    setIsAdding(false)
  }

  console.log(product.options);

  return (
    <>
      <div className="flex flex-col gap-y-4 px-1" ref={actionsRef}>
        <div>
          {(
            <div className="flex flex-col gap-y-3">
              {(product.options || []).map((option) => {
                return (
                  <div key={option.id}>
                    <OptionSelect
                      option={option}
                      current={options[option.title]}
                      updateOption={setOptionValue}
                      title={option.title ?? ""}
                      data-testid="product-options"
                      disabled={!!disabled || isAdding}
                    />
                  </div>
                )
              })}
              <div className="mt-3 w-full">
                <button
                  onClick={randomizeOptions}
                  className="h-8 px-3 text-sm w-full flex items-center justify-center rounded-sm bg-white/10 text-white border border-white/20 hover:bg-white/30"
                  disabled={!!disabled || isAdding}
                  data-testid="randomize-options-button"
                >
                  Randomize Colors
                </button>
              </div>
              <Divider />
            </div>
          )}
        </div>

        <ProductPrice product={product} variant={selectedVariant} />

        <button
          onClick={handleAddToCart}
          disabled={
            !inStock ||
            !selectedVariant ||
            !!disabled ||
            isAdding ||
            !isValidVariant
          }
          className="primary-btn w-full mt-2"
          data-testid="add-product-button"
        >
          {isAdding ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Adding...
            </span>
          ) : (
            (!selectedVariant && !options
              ? "Select variant"
              : !inStock || !isValidVariant
              ? "Out of stock"
              : "Add to cart")
          )}
        </button>

      </div>
    </>
  )
}
